import { useCallback, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { positionReferenceOptionsQuery } from './api';
import { POSITION_FIELDS } from './constants';
import type { PositionReferenceOption } from './types';
import { toWorkdayOption } from '../workdayReferences';

const REFERENCE_TYPES = POSITION_FIELDS.filter(
  (field) => field.referenceType
) as { referenceType: string; hideWhenEmpty?: boolean }[];

/** Keyed on the cached array, so a list is humanised once, not per keystroke. */
const mappedCache = new WeakMap<
  PositionReferenceOption[],
  { id: string; label: string }[]
>();

const toSelectOptions = (options: PositionReferenceOption[]) => {
  const cached = mappedCache.get(options);
  if (cached) return cached;
  const mapped = options.map(toWorkdayOption);
  mappedCache.set(options, mapped);
  return mapped;
};

// The endpoint has no search parameter, so the client narrows the whole list.
const filterOptions = (
  options: PositionReferenceOption[],
  inputValue: string
) => {
  const search = inputValue.trim().toLowerCase();
  const all = toSelectOptions(options);
  // Filter on the rendered label, not the raw name: someone typing
  // "San Francisco" should find `San_Francisco`, and typing a manager's name
  // should find their team.
  return search
    ? all.filter((option) => option.label.toLowerCase().includes(search))
    : all;
};

/**
 * One `loadOptions` per reference type. Uses `fetchQuery` rather than `useQuery`
 * because the selects ask imperatively — seven fields then share one cache and
 * one in-flight request per type.
 */
export const usePositionReferenceOptions = ({
  isOpen = true,
}: { isOpen?: boolean } = {}) => {
  const queryClient = useQueryClient();

  /**
   * Observes the same cache entries the selects fill, rather than copying
   * "came back empty" and "was cut short" into state from inside the loader.
   *
   * Only the fields that have to hide before the form settles are fetched here
   * — otherwise opening the drawer would eagerly pull all seven lists, job
   * profiles included at ~1,750 rows. The rest are `enabled: false`: they never
   * fetch on their own, but still re-render this hook once `loadOptions` has
   * put their list in the cache, which is what keeps the truncation notice
   * honest for lists the recruiter opened by hand.
   */
  // `combine` rather than reading the array in a `useMemo`: the array
  // `useQueries` returns is a new object every render, so a dependency on it
  // never settles. This collapses it to the two facts the form needs, inside
  // the query client's own memoisation.
  const byType = useQueries({
    queries: REFERENCE_TYPES.map((field) => ({
      ...positionReferenceOptionsQuery(field.referenceType),
      enabled: isOpen && !!field.hideWhenEmpty,
    })),
    combine: (results) => {
      const entries: Record<string, { isEmpty: boolean; truncated: boolean }> =
        {};
      REFERENCE_TYPES.forEach((field, index) => {
        const payload = (
          results[index]?.data as { data?: Record<string, unknown> }
        )?.data;
        if (!payload) return;
        entries[field.referenceType] = {
          isEmpty: ((payload.results as unknown[]) || []).length === 0,
          truncated: !!payload.truncated,
        };
      });
      return entries;
    },
  });

  const fetchType = useCallback(
    async (type: string) => {
      const response = await queryClient.fetchQuery(
        positionReferenceOptionsQuery(type)
      );
      return response?.data?.results || [];
    },
    [queryClient]
  );

  const loadOptionsFor = useCallback(
    (type: string) => async (inputValue: string) =>
      filterOptions(await fetchType(type), inputValue),
    [fetchType]
  );

  // `truncated` means the list is genuinely incomplete. Surfaced because
  // silently showing a short list reads as Workday having lost the option the
  // recruiter is looking for.
  const hasTruncatedList = Object.values(byType).some(
    (entry) => entry.truncated
  );

  // Some Workday tenants do not use Company or Cost Centre at all; the form
  // hides those fields rather than showing an empty dropdown.
  const isEmptyList = useCallback(
    (type: string) => !!byType[type]?.isEmpty,
    [byType]
  );

  return useMemo(
    () => ({ loadOptionsFor, hasTruncatedList, isEmptyList }),
    [loadOptionsFor, hasTruncatedList, isEmptyList]
  );
};
